import React from 'react'
import { createPortal } from 'react-dom'
import { NoSymbolIcon } from '@heroicons/react/24/outline'
import { Text } from '../ui'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { isSlotBookable } from '../../services/supportCenterParameters'
import type { SupportCenterSchedulingLimits } from '../../types/supportCenterParameters'
import { DEFAULT_SCHEDULING_LIMITS } from '../../types/supportCenterParameters'
import {
  AGENDA_IN_PROGRESS_HIGHLIGHT_CLASS,
  AGENDA_IN_PROGRESS_LEGEND_SWATCH_CLASS,
  agendaEventAppointmentBadgeClass,
  agendaEventCardClass,
  agendaEventColorLegendItems,
  agendaEventTriageBadgeClass,
  DEFAULT_AGENDA_HOUR_END,
  DEFAULT_AGENDA_HOUR_START,
  isAgendaAttendanceInProgress,
} from './calendarioAgendaHelpers'
import type { SlotOccupancyMap } from './agendaOccupancyHelpers'

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const

function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseScheduled(value: string): Date | null {
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

type SlotTimeState = 'past' | 'current' | 'upcoming'
type SlotTone = 'passado' | 'bloqueado' | 'disponivel' | 'agora'

function getSlotTimeState(day: Date, hour: number, now: Date): SlotTimeState {
  const start = new Date(day)
  start.setHours(hour, 0, 0, 0)
  const end = new Date(day)
  end.setHours(hour + 1, 0, 0, 0)
  if (end.getTime() <= now.getTime()) return 'past'
  if (start.getTime() <= now.getTime()) return 'current'
  return 'upcoming'
}

function resolveSlotTone(timeState: SlotTimeState, bookable: boolean): SlotTone {
  if (timeState === 'past') return 'passado'
  if (!bookable) return 'bloqueado'
  if (timeState === 'current') return 'agora'
  return 'disponivel'
}

/**
 * Contraste moderado (claro e escuro):
 * - Passado: cinza mais escuro
 * - Bloqueado: mesmo fundo de disponível; o ícone vermelho indica o bloqueio
 * - Disponível: fundo do card da grade (branco / gray-800)
 * - Agora: fundo da faixa + pontilhado no slot do momento
 */
function slotCellClass(opts: {
  isToday: boolean
  isCurrentHour: boolean
  isNow: boolean
  tone: SlotTone
  canCreate: boolean
  hasEvents?: boolean
}): string {
  const parts = ['p-1 text-left align-top transition-colors']

  if (opts.isToday) {
    parts.push('border-l-2 border-l-primary-500 dark:border-l-primary-400')
  } else {
    parts.push('border-l border-gray-200 dark:border-gray-700/70')
  }

  if (opts.isCurrentHour) {
    parts.push('border-t-2 border-t-primary-500 dark:border-t-primary-400')
  }

  if (opts.isNow) {
    parts.push(
      '[background-image:radial-gradient(circle,rgba(107,114,128,0.5)_0.35px,transparent_0.75px)]',
      '[background-size:9px_9px]',
      'dark:[background-image:radial-gradient(circle,rgba(156,163,175,0.5)_0.35px,transparent_0.75px)]'
    )
  }

  if (opts.tone === 'passado') {
    parts.push('bg-slate-300 dark:bg-slate-950')
  } else if (opts.tone === 'agora') {
    parts.push('bg-primary-100 dark:bg-primary-900/55')
  } else {
    parts.push('bg-white dark:bg-gray-800')
  }

  if (opts.hasEvents) {
    parts.push('cursor-pointer')
  } else if (opts.tone === 'bloqueado' || opts.tone === 'passado') {
    parts.push('cursor-not-allowed')
  } else if (opts.canCreate) {
    parts.push('hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer')
  } else if (opts.tone === 'disponivel' || opts.tone === 'agora') {
    parts.push('cursor-default')
  }

  return parts.join(' ')
}

export type AgendaTimeGridViewProps = {
  days: Date[]
  events: SupportCenterAgendaEvent[]
  onSelectEvent: (event: SupportCenterAgendaEvent) => void
  /** Id do evento aberto no painel Detalhe — destaca o card na grade. */
  selectedEventId?: string | null
  onSlotClick?: (isoLocal: string) => void
  schedulingLimits?: SupportCenterSchedulingLimits
  /** Primeira hora exibida na grade (inclusive). */
  hourStart?: number
  /** Última hora exibida na grade (inclusive). */
  hourEnd?: number
  /**
   * Ocupação real por célula (`YYYY-MM-DD-H` → free/total).
   * Usada para pintar disponível/bloqueado e impedir clique; o badge numérico não é exibido.
   */
  slotOccupancy?: SlotOccupancyMap | null
  /**
   * Quando false (filtro "Todos"), células lotadas por agendamento (free=0, total>0)
   * ficam visualmente disponíveis; o vermelho fica só para antecedência / sem grade.
   * Default: true (filtro de um profissional).
   */
  paintFullyBookedAsBlocked?: boolean
  /** Incrementar para rolar a grade até o horário atual. */
  scrollToNowToken?: number
  /** Controles (visão, navegação) que ficam fixos junto com o cabeçalho dos dias. */
  toolbar?: React.ReactNode
}

export const AgendaTimeGridView: React.FC<AgendaTimeGridViewProps> = ({
  days,
  events,
  onSelectEvent,
  selectedEventId = null,
  onSlotClick,
  schedulingLimits = DEFAULT_SCHEDULING_LIMITS,
  hourStart = DEFAULT_AGENDA_HOUR_START,
  hourEnd = DEFAULT_AGENDA_HOUR_END,
  slotOccupancy = null,
  paintFullyBookedAsBlocked = true,
  scrollToNowToken = 0,
  toolbar,
}) => {
  const safeStart = Math.min(hourStart, hourEnd)
  const safeEnd = Math.max(hourStart, hourEnd)
  const hours = Array.from({ length: safeEnd - safeStart + 1 }, (_, i) => safeStart + i)
  const [now, setNow] = React.useState(() => new Date())
  const todayKey = toDateKey(now)
  const rangeIncludesToday = days.some((day) => toDateKey(day) === todayKey)
  const currentHour = now.getHours()
  const nowHourRowRef = React.useRef<HTMLDivElement | null>(null)
  const scrollHour =
    hours.length === 0
      ? null
      : hours.reduce((best, hour) =>
          Math.abs(hour - currentHour) < Math.abs(best - currentHour) ? hour : best
        )
  const minHoursAhead = schedulingLimits.minHoursAhead
  const minHoursAheadLabel = minHoursAhead === 1 ? '1 hora' : `${minHoursAhead} horas`
  const blockedLegendLabel = `Bloqueado (Antecedência mínima: ${minHoursAheadLabel})`
  const blockedLegendTitle =
    'Agenda bloqueada pela configuração de antecedência mínima em horas para marcar um horário'
  const hasOccupancy = Boolean(slotOccupancy && slotOccupancy.size > 0)
  const dayCount = Math.max(1, days.length)
  const isDayView = dayCount === 1
  const gridTemplateColumns = `2.5rem repeat(${dayCount}, minmax(0, 1fr))`
  const minWidthClass = isDayView ? 'min-w-[320px]' : 'min-w-[640px]'

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  React.useLayoutEffect(() => {
    if (!scrollToNowToken) return
    nowHourRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [scrollToNowToken, scrollHour])

  const eventsByDayHour = React.useMemo(() => {
    const map = new Map<string, SupportCenterAgendaEvent[]>()
    for (const event of events) {
      const when = parseScheduled(event.scheduled_at)
      if (!when) continue
      const key = `${toDateKey(when)}-${when.getHours()}`
      const list = map.get(key) ?? []
      list.push(event)
      map.set(key, list)
    }
    return map
  }, [events])

  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const legendRef = React.useRef<HTMLDivElement | null>(null)
  const [legendPinned, setLegendPinned] = React.useState(false)
  const [legendBox, setLegendBox] = React.useState({ left: 0, width: 0 })
  const [legendHeight, setLegendHeight] = React.useState(40)
  const [blockedTooltip, setBlockedTooltip] = React.useState<{
    x: number
    y: number
    content: React.ReactNode
  } | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false)
  const shortcutsButtonRef = React.useRef<HTMLButtonElement>(null)
  const shortcutsPopoverRef = React.useRef<HTMLDivElement>(null)
  const [shortcutsPos, setShortcutsPos] = React.useState<{
    bottom: number
    right: number
  } | null>(null)

  const updateShortcutsPos = React.useCallback(() => {
    const button = shortcutsButtonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    setShortcutsPos({
      bottom: Math.max(8, window.innerHeight - rect.top + 8),
      right: Math.max(8, window.innerWidth - rect.right),
    })
  }, [])

  React.useEffect(() => {
    if (!shortcutsOpen) return
    updateShortcutsPos()
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (shortcutsButtonRef.current?.contains(target)) return
      if (shortcutsPopoverRef.current?.contains(target)) return
      setShortcutsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      setShortcutsOpen(false)
    }
    const onReposition = () => updateShortcutsPos()
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [shortcutsOpen, updateShortcutsPos])

  const syncLegendPin = React.useCallback(() => {
    const root = rootRef.current
    if (!root) return
    const rect = root.getBoundingClientRect()
    const vh = window.innerHeight
    const visible = rect.top < vh && rect.bottom > 0
    const pin = visible && rect.bottom > vh
    setLegendPinned(pin)
    setLegendBox({ left: rect.left, width: rect.width })
    const legendEl = legendRef.current
    if (legendEl) {
      setLegendHeight(Math.max(36, Math.ceil(legendEl.getBoundingClientRect().height)))
    }
  }, [])

  React.useEffect(() => {
    const main = rootRef.current?.closest('main')
    const onScrollOrResize = () => {
      setBlockedTooltip(null)
      syncLegendPin()
    }
    main?.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)
    syncLegendPin()
    const raf = window.requestAnimationFrame(syncLegendPin)
    return () => {
      main?.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
      window.cancelAnimationFrame(raf)
    }
  }, [syncLegendPin, days, hours.length, events.length])

  const legendContent = (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 px-3 py-2 text-[11px] text-gray-600 dark:text-gray-300">
      <span className="inline-flex items-center gap-1">
        <span className="h-3 w-3 rounded-sm bg-white ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-500" />
        Disponível
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-3 w-3 rounded-sm bg-slate-300 ring-1 ring-slate-500 dark:bg-slate-950 dark:ring-slate-700" />
        Passado
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="h-3 w-3 rounded-sm bg-primary-100 ring-1 ring-primary-400 dark:bg-primary-900/55 dark:ring-primary-400 [background-image:radial-gradient(circle,rgba(107,114,128,0.5)_0.35px,transparent_0.7px)] [background-size:4px_4px] dark:[background-image:radial-gradient(circle,rgba(156,163,175,0.5)_0.35px,transparent_0.7px)]"
          aria-hidden="true"
        />
        Hoje
      </span>
      <span className="hidden sm:inline-block h-3 w-px bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
      {agendaEventColorLegendItems().map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1">
          <span className={`h-2.5 w-2.5 rounded-sm ${item.swatchClass}`} />
          {item.label}
        </span>
      ))}
      <span
        className="inline-flex items-center gap-1"
        title="Atendimento com data de início e sem data de fim"
      >
        <span className={AGENDA_IN_PROGRESS_LEGEND_SWATCH_CLASS} aria-hidden="true" />
        Em andamento
      </span>
      <span className="hidden sm:inline-block h-3 w-px bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
      <span className="inline-flex items-center gap-1" title="Status da triagem">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Tr
        </span>
        Triagem
      </span>
      <span className="inline-flex items-center gap-1" title="Status do atendimento">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          At
        </span>
        Atendimento
      </span>
      <button
        type="button"
        ref={shortcutsButtonRef}
        onClick={() => {
          setShortcutsOpen((prev) => {
            const next = !prev
            if (next) requestAnimationFrame(() => updateShortcutsPos())
            return next
          })
        }}
        aria-expanded={shortcutsOpen}
        aria-haspopup="dialog"
        className={`ml-auto inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline ${
          shortcutsOpen
            ? 'text-primary-700 dark:text-primary-300'
            : 'text-primary-600 dark:text-primary-400'
        }`}
      >
        Atalhos
      </button>
    </div>
  )

  return (
    <div ref={rootRef} className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className={`${minWidthClass} relative`}>
        <div className="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-md">
          {toolbar ? (
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              {toolbar}
            </div>
          ) : null}
          <div
            className="grid border-b border-gray-200 dark:border-gray-700"
            style={{ gridTemplateColumns }}
          >
            <div
              className="relative overflow-hidden bg-white dark:bg-gray-800"
              aria-hidden="true"
            >
              <svg
                className="absolute inset-0 h-full w-full text-gray-300 dark:text-gray-600"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <line
                  x1="0"
                  y1="0"
                  x2="100"
                  y2="100"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              <span className="absolute right-2 top-1 text-[9px] font-medium leading-none text-gray-500 dark:text-gray-400">
                Dia
              </span>
              <span className="absolute bottom-0.5 left-0.5 text-[9px] font-medium leading-none text-gray-500 dark:text-gray-400">
                Hora
              </span>
            </div>
            {days.map((day) => {
              const isToday = toDateKey(day) === todayKey
              return (
                <div
                  key={toDateKey(day)}
                  className={`flex flex-col items-center justify-center gap-0.5 p-2 text-center border-l ${
                    isToday
                      ? 'border-gray-200 dark:border-gray-700 bg-primary-600 dark:bg-primary-700'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  }`}
                >
                  {isToday ? (
                    <div className="flex items-center justify-center gap-1.5">
                      <span className="text-xs font-medium text-white/90">
                        {WEEKDAY_LABELS[day.getDay()]}
                      </span>
                      <span className="text-sm font-bold text-white">{day.getDate()}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-1.5">
                      <Text size="xs" color="muted">
                        {WEEKDAY_LABELS[day.getDay()]}
                      </Text>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {day.getDate()}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div>
          {hours.map((hour) => {
          const hourLabel = String(hour).padStart(2, '0')
          const isCurrentHour = rangeIncludesToday && hour === currentHour
          const rowHasEvents = days.some(
            (day) => (eventsByDayHour.get(`${toDateKey(day)}-${hour}`) ?? []).length > 0
          )
          const rowMinHeight = rowHasEvents ? 72 : 36
          return (
            <div
              key={hour}
              ref={hour === scrollHour ? nowHourRowRef : undefined}
              className="grid border-b border-gray-200 dark:border-gray-700/60"
              style={{ gridTemplateColumns, minHeight: rowMinHeight }}
            >
              <div
                className={`flex flex-col items-center justify-center px-0.5 py-1 ${
                  isCurrentHour
                    ? 'bg-primary-600 dark:bg-primary-700'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {isCurrentHour ? (
                  <span className="text-sm font-bold text-white">{hourLabel}</span>
                ) : (
                  <span className="text-sm font-semibold">{hourLabel}</span>
                )}
              </div>
              {days.map((day) => {
                const key = `${toDateKey(day)}-${hour}`
                const cellEvents = eventsByDayHour.get(key) ?? []
                const isoLocal = `${toDateKey(day)}T${String(hour).padStart(2, '0')}:00`
                const limitsOk = isSlotBookable(isoLocal, schedulingLimits, now)
                const occupancy = slotOccupancy?.get(key)
                const occupancyFree =
                  occupancy != null ? occupancy.free > 0 : !hasOccupancy
                const noGradeOnSlot = occupancy != null && occupancy.total === 0
                const fullyBooked =
                  occupancy != null && occupancy.free <= 0 && occupancy.total > 0
                const bookable = limitsOk && occupancyFree
                const canCreate = Boolean(onSlotClick) && bookable
                /** Cor: no filtro Todos, lotado por agendamento não usa vermelho de bloqueio. */
                const visuallyBookable = paintFullyBookedAsBlocked
                  ? bookable
                  : limitsOk && !noGradeOnSlot
                const isToday = toDateKey(day) === todayKey
                const timeState = getSlotTimeState(day, hour, now)
                const tone = resolveSlotTone(timeState, visuallyBookable)
                const blockedByMinAhead = !limitsOk && timeState !== 'past'
                const blockedTitle = blockedByMinAhead
                  ? blockedLegendTitle
                  : noGradeOnSlot
                    ? 'Nenhum profissional com este horário na grade'
                    : fullyBooked
                      ? 'Sem profissional livre neste horário'
                      : 'Horário indisponível para agendamento'
                const showBlockedIcon = tone === 'bloqueado' && cellEvents.length === 0
                const blockedTooltipContent = blockedByMinAhead ? (
                  <div>
                    <div className="font-semibold">{blockedLegendLabel}</div>
                    <div className="mt-0.5 opacity-90">{blockedLegendTitle}</div>
                  </div>
                ) : (
                  blockedTitle
                )
                return (
                  <button
                    key={key}
                    type="button"
                    title={
                      tone === 'passado'
                        ? 'Horário já passou'
                        : canCreate
                          ? 'Clique para agendar neste horário'
                          : fullyBooked && tone !== 'bloqueado'
                            ? 'Sem profissional livre neste horário'
                            : undefined
                    }
                    onMouseEnter={(e) => {
                      if (!showBlockedIcon) return
                      const rect = e.currentTarget.getBoundingClientRect()
                      setBlockedTooltip({
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                        content: blockedTooltipContent,
                      })
                    }}
                    onMouseLeave={() => {
                      if (showBlockedIcon) setBlockedTooltip(null)
                    }}
                    onClick={() => {
                      if (canCreate) onSlotClick?.(isoLocal)
                    }}
                    className={`relative isolate h-full w-full ${slotCellClass({
                      isToday,
                      isCurrentHour,
                      isNow: isToday && timeState === 'current',
                      tone,
                      canCreate,
                      hasEvents: cellEvents.length > 0,
                    })}`}
                  >
                    {showBlockedIcon ? (
                      <NoSymbolIcon
                        className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 text-rose-600 dark:text-rose-400"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative z-10 space-y-1">
                      {cellEvents.map((event) => {
                        const label =
                          event.patient_people?.name ||
                          event.holder_people?.name ||
                          event.patient_cpf
                        const professionalName =
                          event.professional?.name?.trim() || null
                        const triageLabel = event.triage_status
                          ? TRIAGE_STATUS_LABELS[event.triage_status]
                          : event.triage_id
                            ? TRIAGE_STATUS_LABELS.pendente
                            : 'Sem triagem'
                        const isSelected = selectedEventId === event.id
                        const inProgress = isAgendaAttendanceInProgress(event)
                        return (
                          <div
                            key={event.id}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            onClick={(e) => {
                              e.stopPropagation()
                              onSelectEvent(event)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                e.stopPropagation()
                                onSelectEvent(event)
                              }
                            }}
                            className={`rounded-md px-1.5 py-1.5 text-left cursor-pointer border-l-[3px] ${agendaEventCardClass(
                              event
                            )} ${inProgress ? AGENDA_IN_PROGRESS_HIGHLIGHT_CLASS : ''} ${
                              isSelected
                                ? 'opacity-100 ring-2 ring-primary-600 ring-offset-1 ring-offset-white dark:ring-primary-400 dark:ring-offset-gray-800 shadow-md'
                                : inProgress
                                  ? 'opacity-100'
                                  : tone === 'passado'
                                    ? 'opacity-50 hover:opacity-100'
                                    : ''
                            }`}
                            title={`Pac ${label}${professionalName ? ` · Pro ${professionalName}` : ''} · ${triageLabel} · ${APPOINTMENT_STATUS_LABELS[event.status]}${
                              inProgress ? ' · Em andamento' : ''
                            }${isSelected ? ' · Selecionado' : ''}`}
                          >
                            {isDayView ? (
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="min-w-0 flex-1 leading-tight">
                                  <div className="flex items-center gap-1 min-w-0">
                                    <span
                                      className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                      title="Paciente"
                                    >
                                      Pac
                                    </span>
                                    <div className="min-w-0 flex-1 text-[11px] font-semibold text-gray-900 dark:text-white truncate">
                                      {label}
                                    </div>
                                  </div>
                                  {professionalName ? (
                                    <div className="flex items-center gap-1 min-w-0">
                                      <span
                                        className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                        title="Profissional"
                                      >
                                        Pro
                                      </span>
                                      <div
                                        className="min-w-0 flex-1 text-[10px] text-gray-600 dark:text-gray-300 truncate"
                                        title={professionalName}
                                      >
                                        {professionalName}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-1 shrink-0 min-w-0">
                                  <span
                                    className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                    title="Status da triagem"
                                  >
                                    Tr
                                  </span>
                                  <span
                                    className={`inline-flex justify-center px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-4 whitespace-nowrap ${agendaEventTriageBadgeClass(
                                      event.triage_status ??
                                        (event.triage_id ? 'pendente' : null)
                                    )}`}
                                  >
                                    {triageLabel}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 shrink-0 min-w-0">
                                  <span
                                    className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                    title="Status do atendimento"
                                  >
                                    At
                                  </span>
                                  <span
                                    className={`inline-flex justify-center px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-4 whitespace-nowrap ${agendaEventAppointmentBadgeClass(
                                      event.status
                                    )}`}
                                  >
                                    {APPOINTMENT_STATUS_LABELS[event.status]}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-1 min-w-0">
                                  <span
                                    className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                    title="Paciente"
                                  >
                                    Pac
                                  </span>
                                  <div className="min-w-0 flex-1 text-[11px] font-semibold text-gray-900 dark:text-white truncate leading-tight">
                                    {label}
                                  </div>
                                </div>
                                {professionalName ? (
                                  <div className="flex items-center gap-1 min-w-0">
                                    <span
                                      className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                      title="Profissional"
                                    >
                                      Pro
                                    </span>
                                    <div
                                      className="min-w-0 flex-1 text-[10px] text-gray-600 dark:text-gray-300 truncate leading-tight"
                                      title={professionalName}
                                    >
                                      {professionalName}
                                    </div>
                                  </div>
                                ) : null}
                                <div className="mt-1 flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1 min-w-0">
                                    <span
                                      className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                      title="Status da triagem"
                                    >
                                      Tr
                                    </span>
                                    <span
                                      className={`min-w-0 flex-1 inline-flex justify-center px-1 py-0.5 rounded-full text-[10px] font-medium leading-4 truncate ${agendaEventTriageBadgeClass(
                                        event.triage_status ??
                                          (event.triage_id ? 'pendente' : null)
                                      )}`}
                                    >
                                      {triageLabel}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 min-w-0">
                                    <span
                                      className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                      title="Status do atendimento"
                                    >
                                      At
                                    </span>
                                    <span
                                      className={`min-w-0 flex-1 inline-flex justify-center px-1 py-0.5 rounded-full text-[10px] font-medium leading-4 truncate ${agendaEventAppointmentBadgeClass(
                                        event.status
                                      )}`}
                                    >
                                      {APPOINTMENT_STATUS_LABELS[event.status]}
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </button>
                )
              })}
            </div>
          )
          })}
        </div>

        {legendPinned ? <div aria-hidden style={{ height: legendHeight }} /> : null}
        <div
          ref={legendRef}
          className="z-20 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.35)]"
          style={
            legendPinned
              ? {
                  position: 'fixed',
                  bottom: 0,
                  left: legendBox.left,
                  width: legendBox.width,
                }
              : undefined
          }
        >
          {legendContent}
        </div>
        {blockedTooltip
          ? createPortal(
              <div
                className="fixed z-[9999] max-w-[280px] -translate-x-1/2 -translate-y-full px-3 py-2 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg pointer-events-none"
                style={{ left: blockedTooltip.x, top: blockedTooltip.y - 8 }}
              >
                {blockedTooltip.content}
              </div>,
              document.body
            )
          : null}
        {shortcutsOpen &&
          shortcutsPos &&
          createPortal(
            <div
              ref={shortcutsPopoverRef}
              role="dialog"
              aria-label="Atalhos de teclado"
              className="fixed z-[100] w-64 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 p-3 space-y-2"
              style={{ bottom: shortcutsPos.bottom, right: shortcutsPos.right }}
            >
              <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                Atalhos de teclado
              </Text>
              <ul className="space-y-1.5 text-xs text-gray-700 dark:text-gray-200">
                {(
                  [
                    { keys: 'T', label: 'Ir para hoje' },
                    { keys: '← / →', label: 'Trocar período' },
                    { keys: 'D', label: 'Visão dia' },
                    { keys: 'S', label: 'Visão semana' },
                    { keys: 'Esc', label: 'Fechar detalhe' },
                  ] as const
                ).map((item) => (
                  <li key={item.keys} className="flex items-center justify-between gap-3">
                    <span>{item.label}</span>
                    <kbd className="inline-flex min-w-[2.5rem] justify-center rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-800 dark:text-gray-100">
                      {item.keys}
                    </kbd>
                  </li>
                ))}
              </ul>
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}
